import React, { Fragment, useEffect, useState } from 'react'

import { HelpCircle as HelpCircleIcon } from 'react-feather'
import { Box, Button, Fade, Grid, Typography } from '@material-ui/core'

import {
  BaseStatusCard,
  Container,
  ContentHeader,
  DashboardTabs,
  DriveTour,
  FilterButton,
  Page,
} from 'components'
import {
  DataIntelligenceFilter,
  GeneralInformation,
  LegalFramework,
  Safeguard,
  SharedData,
  TreatedData,
  TreatmentAgent,
} from './components'

import helpers from 'helpers'

import useAuth from 'hooks/useAuth'
import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import useDataProcessOptions from 'hooks/useDataProcessOptions'

import constants from 'constants/index'
import * as service from 'service'
import theme from 'theme'

const DataIntelligenceMain = () => {
  const {
    DASHBOARD_GENERAL_INFORMATION_TAB,
    DASHBOARD_MAIN_TAB,
    DASHBOARD_SAFEGUARD_TAB,
    DASHBOARD_SHARED_DATA_TAB,
    DASHBOARD_TAB_ITEMS,
    DASHBOARD_TREATMENT_AGENT_TAB,
    TITULAR_AGE_RANGES_CARD_ITEMS,
  } = constants.dataIntelligence

  const [openTour, setOpenTour] = useState(false)

  const { handleStatusCard } = helpers.baseStatusCard

  const [currentTab, setCurrentTab] = useState(DASHBOARD_MAIN_TAB)

  const auth = useAuth()
  const filter = useFilter()
  const { loadData, isLoading: isLoadingDataProcessOptions } =
    useDataProcessOptions()

  const { response, isLoading } = useFetch(
    service.dponet.dataIntelligence.get,
    { ...filter.filters },
    [filter.filters],
  )

  const dataIntelligence = response?.data?.dataIntelligence || {}

  const TAB_COMPONENTS = {
    [DASHBOARD_GENERAL_INFORMATION_TAB]: (
      <Fragment>
        <GeneralInformation
          isLoading={isLoading}
          generalInformation={dataIntelligence?.generalInformation}
        />
        <TreatedData
          isLoading={isLoading}
          treatedData={dataIntelligence?.treatedData}
        />
      </Fragment>
    ),
    [DASHBOARD_SAFEGUARD_TAB]: (
      <Safeguard
        isLoading={isLoading}
        safeguard={dataIntelligence?.safeguard}
      />
    ),
    [DASHBOARD_SHARED_DATA_TAB]: (
      <SharedData
        isLoading={isLoading}
        sharedData={dataIntelligence?.sharedData}
      />
    ),
    [DASHBOARD_TREATMENT_AGENT_TAB]: (
      <Fragment>
        <LegalFramework
          isLoading={isLoading}
          legalFramework={dataIntelligence?.legalFramework}
        />
        <TreatmentAgent
          isLoading={isLoading}
          treatmentAgent={dataIntelligence?.treatmentAgent}
        />
      </Fragment>
    ),
  }

  const handleTabsChange = (newValue) => setCurrentTab(newValue)

  const handleOpenTour = () => setOpenTour((prev) => !prev)

  const baseStatusCardItems = TITULAR_AGE_RANGES_CARD_ITEMS.map((item) => ({
    ...item,
    title:
      dataIntelligence?.titularAgeRanges?.find(
        (age) => age.id === item.titularAgeRanges,
      )?.quantity || 0,
  }))

  const loadDataProcessOptions = async () => await loadData(auth?.company?.id)

  useEffect(() => {
    loadDataProcessOptions()
  }, [])

  return (
    <Page title="Inteligência de dados">
      <Container maxWidth={false}>
        <ContentHeader title="Inteligência de dados">
          <Button
            variant="contained"
            color="primary"
            startIcon={<HelpCircleIcon size={20} />}
            onClick={handleOpenTour}
            id={constants.dataIntelligence.DATA_INTELLIGENCE_MAIN_DRIVER_STEP_0}
            fullWidth
          >
            Tutorial
          </Button>
          <FilterButton
            setDrawerOpen={filter.setDrawerOpen}
            isLoading={isLoadingDataProcessOptions}
          />
        </ContentHeader>
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(4)}>
          <DashboardTabs
            tabs={DASHBOARD_TAB_ITEMS}
            onChange={handleTabsChange}
            currentTab={currentTab}
          />
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            <Typography variant="h4" color="primary">
              Processos por faixa etária
            </Typography>
            <Grid container spacing={2}>
              {baseStatusCardItems.map((item) => {
                return (
                  <Grid item xs={12} sm={6} md={3} key={item.titularAgeRanges}>
                    <BaseStatusCard
                      {...item}
                      title={item?.title ?? 'Indefinido'}
                      selected={filter.filters?.titularAgeRanges?.includes(
                        String(item?.titularAgeRanges),
                      )}
                      onClick={() =>
                        handleStatusCard({
                          filter,
                          key: 'titularAgeRanges',
                          value: String(item?.titularAgeRanges),
                          multiSelect: true,
                        })
                      }
                      titleProps={{ variant: 'h2' }}
                      isLoading={isLoading}
                      titleResponse
                    />
                  </Grid>
                )
              })}
            </Grid>
          </Box>
          <Fade in={true} timeout={400} key={currentTab}>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(4)}
            >
              {currentTab === DASHBOARD_MAIN_TAB
                ? Object.values(TAB_COMPONENTS).map((Component, idx) => (
                    <Fragment key={idx}>{Component}</Fragment>
                  ))
                : TAB_COMPONENTS[currentTab]}
            </Box>
          </Fade>
        </Box>
      </Container>
      {!isLoadingDataProcessOptions && filter.drawerOpen && (
        <DataIntelligenceFilter filter={filter} />
      )}
      <DriveTour
        stepsMatrix={
          constants.dataIntelligence.DATA_INTELLIGENCE_MAIN_DRIVER_STEPS_OBJECT
        }
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default DataIntelligenceMain
